import { useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { useTranslation } from "react-i18next";
import { successToast, errorToast } from "@idwal/idwal-react-components";
import { requestPurchase } from "../../services/RequestPurchaseService";
import { EmailTypes } from "../../constants/emails";
import { sendGAEvent } from "../../services/AnalyticsService";
import {
    GA_BUTTON_EVENT,
    GA_SHARE_SEND_LINK
} from "../../constants/GaEvents";

type Props = {
    visible: boolean;
    onHide: () => void;
    inspectionId: string;
    onManageSharing: () => void;
};

const isValidEmail = (v: string) => {
    const i = v.indexOf("@");
    return i > 0 && v.indexOf(".", i) > i + 1;
};

export const ShareModal = ({
    visible,
    onHide,
    inspectionId,
    onManageSharing
}: Props) => {
    const { t } = useTranslation("locale");

    const [nameValue, setNameValue] = useState("");
    const [organisationValue, setOrganisationValue] = useState("");
    const [emailValue, setEmailValue] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<{
        name?: string;
        organisation?: string;
        email?: string;
    }>({});

    const resetAndClose = () => {
        setNameValue("");
        setOrganisationValue("");
        setEmailValue("");
        setErrors({});
        onHide();
    };

    const onSubmit = async () => {
        const newErrors = {
            name: !nameValue.trim() ? t("validation.required.name") : undefined,
            organisation: !organisationValue.trim()
                ? t("validation.required.organisation")
                : undefined,
            email: !isValidEmail(emailValue.trim())
                ? t("validation.required.email")
                : undefined
        };
        setErrors(newErrors);
        if (newErrors.name || newErrors.organisation || newErrors.email) {
            return;
        }

        setSubmitting(true);
        const success = await requestPurchase({
            inspectionId,
            email: emailValue.trim(),
            name: nameValue.trim(),
            organisation: organisationValue.trim(),
            emailType: EmailTypes.SHARE_PAYMENT_LINK
        });
        setSubmitting(false);

        if (success) {
            sendGAEvent(GA_BUTTON_EVENT, GA_SHARE_SEND_LINK);
            successToast(t("shareModal.successToast"));
            resetAndClose();
        } else {
            errorToast(t("shareModal.errorToast"));
        }
    };

    const footer = (
        <div className="flex justify-content-between align-items-center">
            <Button
                label={t("shareModal.manageSharing")}
                icon="pi pi-users"
                link
                onClick={onManageSharing}
            />
            <Button
                label={t("shareModal.sendLink")}
                icon="pi pi-send"
                onClick={onSubmit}
                loading={submitting}
            />
        </div>
    );

    return (
        <Dialog
            header={t("shareModal.title")}
            visible={visible}
            onHide={resetAndClose}
            footer={footer}
            draggable={false}
            style={{ width: "min(680px, 95vw)" }}
        >
            <p>{t("shareModal.body")}</p>
            <div className="flex flex-column gap-3 mt-3">
                <div className="flex align-items-start gap-3">
                    <label className="w-8rem mt-2">
                        {t("shareModal.email")}
                    </label>
                    <div className="flex flex-column flex-1">
                        <InputText
                            value={emailValue}
                            maxLength={320}
                            onChange={(e) => setEmailValue(e.target.value)}
                            className={`w-full${errors.email ? " p-invalid" : ""}`}
                        />
                        {errors.email ? (
                            <small className="p-error">{errors.email}</small>
                        ) : (
                            <small>{t("shareModal.emailHelper")}</small>
                        )}
                    </div>
                </div>
                <div className="flex align-items-center gap-3">
                    <label className="w-8rem">{t("shareModal.name")}</label>
                    <div className="flex flex-column flex-1">
                        <InputText
                            value={nameValue}
                            maxLength={255}
                            onChange={(e) => setNameValue(e.target.value)}
                            className={`w-full${errors.name ? " p-invalid" : ""}`}
                        />
                        {errors.name && (
                            <small className="p-error">{errors.name}</small>
                        )}
                    </div>
                </div>
                <div className="flex align-items-center gap-3">
                    <label className="w-8rem">
                        {t("shareModal.organisation")}
                    </label>
                    <div className="flex flex-column flex-1">
                        <InputText
                            value={organisationValue}
                            maxLength={255}
                            onChange={(e) =>
                                setOrganisationValue(e.target.value)
                            }
                            className={`w-full${
                                errors.organisation ? " p-invalid" : ""
                            }`}
                        />
                        {errors.organisation && (
                            <small className="p-error">
                                {errors.organisation}
                            </small>
                        )}
                    </div>
                </div>
            </div>
        </Dialog>
    );
};
